import { AbstractControl, FormControl, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { DropdownOptions } from 'app/core/components/dropdown-selection/dropdown-selection.component';
import { SchoolActivity, SchoolActivityInstance, SchoolActivityState } from 'app/models';
import { FlexPeriod, FlexVisibility, SignUpWindowSessions } from 'app/services/flex-period.service';
import { InstanceTimes } from 'app/services/school-activity.service';
import { TimeRangeInputType } from 'app/shared/time-range-input/time-range-input/time-range-input.component';
import * as moment from 'moment-timezone';
import { RRule, Weekday } from 'rrule';

export const SCHEDULE_TYPE_KEY = 'scheduleType';
export const SCHEDULE_REPEAT_END_DATE_KEY = 'repeatEndDate';
export const FLEX_PERIOD_ID_KEY = 'flexPeriodId';
export const FLEX_PERIOD_REPEATS_KEY = 'flexPeriodRepeats';
export const FLEX_INSTANCE_TIMES_KEY = 'flexInstanceTimes';
export const CUSTOM_SCHEDULE_DATE_KEY = 'customStartDate';
export const CUSTOM_SCHEDULE_TIME_RANGE_KEY = 'customTimeRange';
export const CUSTOM_SCHEDULE_REPEAT_KEY = 'customRepeat';
export const VISIBILITY_KEY = 'visibility';
export const VISIBILITY_STUDENT_IDS_KEY = 'visibilityStudentIds';
export const VISIBILITY_GROUP_IDS_KEY = 'visibilityGroupIds';

export const DEFAULT_REPEAT_END_DATE = moment().add(4, 'months');

export const activityScheduleFormFields = {
	[SCHEDULE_TYPE_KEY]: new FormControl<'flex_period' | 'custom'>('flex_period', {
		nonNullable: true,
		validators: Validators.required,
	}),
	[SCHEDULE_REPEAT_END_DATE_KEY]: new FormControl<moment.Moment | undefined>(undefined, null),

	// Flex period
	[FLEX_PERIOD_REPEATS_KEY]: new FormControl<SchoolActivityState>('flex_recurring', Validators.required),
	[FLEX_PERIOD_ID_KEY]: new FormControl<number | undefined>(undefined, Validators.required),
	[FLEX_INSTANCE_TIMES_KEY]: new FormControl<InstanceTimes[]>([], null),

	// Custom scheduling
	[CUSTOM_SCHEDULE_DATE_KEY]: new FormControl(moment(), null),
	[CUSTOM_SCHEDULE_TIME_RANGE_KEY]: new FormControl<TimeRangeInputType | undefined>(undefined, null),
	[CUSTOM_SCHEDULE_REPEAT_KEY]: new FormControl<string | undefined>(undefined, null),
};

export function addActivityScheduleFormFields(form: UntypedFormGroup) {
	Object.entries(activityScheduleFormFields).forEach(([key, control]) => {
		form.addControl(key, control);
	});
}

//#endregion

//#region Form validators

export function flexScheduleRepeatEndValidator(form: FormGroup, instances: SchoolActivityInstance[]): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (form.value[SCHEDULE_TYPE_KEY] != 'flex_period') {
			return null;
		}
		if (instances.length === 0) {
			return null;
		}

		const firstScheduled = instances.sort((a, b) => a.start_time.localeCompare(b.start_time))[0];
		if (firstScheduled) {
			const controlDate = moment(control.value).startOf('day');
			const firstScheduledDate = moment(new Date(firstScheduled.start_time)).startOf('day');
			if (controlDate < firstScheduledDate) {
				return { afterStart: 'Set a date after ' + firstScheduledDate.format('MMM D, YYYY') };
			}
		}
		return null;
	};
}

export function customScheduleRepeatEndValidator(form: FormGroup): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (form.value[SCHEDULE_TYPE_KEY] != 'custom') {
			return null;
		}
		if (!control.value) {
			return { required: 'This field is required' };
		}

		if (control.value < form.value[CUSTOM_SCHEDULE_DATE_KEY]) {
			return { afterStart: 'Set a date after ' + form.value[CUSTOM_SCHEDULE_DATE_KEY].format('MMM D, YYYY') };
		}

		const today = moment().startOf('day');
		const controlDate = moment(control.value).startOf('day');
		if (controlDate.isBefore(today)) {
			return { afterStart: 'Set a date after ' + today.format('MMM D, YYYY') };
		}

		return null;
	};
}

export function studentVisibilityValidator(form: FormGroup): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const studentIds = control.value[VISIBILITY_STUDENT_IDS_KEY];
		const groupIds = control.value[VISIBILITY_GROUP_IDS_KEY];
		const hasStudent = Array.isArray(studentIds) && studentIds.length > 0;
		const hasGroup = Array.isArray(groupIds) && groupIds.length > 0;
		return hasStudent || hasGroup ? null : { atLeastOneRequired: true };
	};
}

export function updateVisibilityFormValidity(formGroup: FormGroup): void {
	if (formGroup?.get([VISIBILITY_KEY])?.value === FlexVisibility.Limited) {
		formGroup?.addValidators(studentVisibilityValidator(formGroup));
	} else {
		const currentValidators = formGroup?.validator ? [formGroup.validator] : [];
		const filteredValidators = currentValidators.filter((v) => v !== studentVisibilityValidator(formGroup));
		formGroup?.setValidators(filteredValidators.length > 0 ? filteredValidators : null);
	}
	formGroup?.updateValueAndValidity();
}

export function maxCapacityValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value;
	const num = Number(value);
	if (num <= 0) {
		return { maxCapacityError: true };
	}
	return null;
}

//#endregion

//#region Data conversion

export function getActivityStartDate(activity: SchoolActivity, tz: string): moment.Moment {
	return activity.custom_start_time ? moment.tz(activity.custom_start_time, tz) : moment();
}

export function getActivityTimeRange(activity: SchoolActivity, tz: string): TimeRangeInputType | null {
	if (activity.custom_start_time && activity.custom_end_time) {
		const start = moment.tz(activity.custom_start_time, tz).format('HH:mm');
		const end = moment.tz(activity.custom_end_time, tz).format('HH:mm');
		return {
			start,
			end,
		};
	}

	return null;
}

export function getCustomStartEndTime(
	date: moment.Moment,
	timeRange: TimeRangeInputType
): {
	startTime: moment.Moment;
	endTime: moment.Moment;
} {
	const startTime = moment(date.format('YYYY-MM-DD') + ' ' + timeRange.start);
	const endTime = moment(date.format('YYYY-MM-DD') + ' ' + timeRange.end);
	return { startTime, endTime };
}

/**
 * Returns start and end times for a given date based on a FlexPeriod schedule.
 * Useful when schedules are defined by weekdays and hour/minute only (no fixed date).
 */
export function getFlexStartEndTime(flexPeriod: FlexPeriod, date: Date): { start: string; end: string } {
	const weekDay = date.getDay();
	const scheduleForWeekday = flexPeriod.schedules.find((s) => s.days_of_week.includes(weekDay)) || flexPeriod.schedules[0];

	const startDate = new Date(date);
	startDate.setHours(scheduleForWeekday.start_hour, scheduleForWeekday.start_minute, 0, 0);

	const endDate = new Date(date);
	endDate.setHours(scheduleForWeekday.end_hour, scheduleForWeekday.end_minute, 0, 0);

	return { start: startDate.toString(), end: endDate.toString() };
}

// Instance list helpers

export const getUpcomingCustomInstanceTimes = (
	startTimeIso: string,
	endTimeIso: string,
	repeatRule: string | null | undefined,
	repeatEndDateIso: string | null | undefined
): InstanceTimes[] => {
	const upcomingTimes: InstanceTimes[] = [];

	const startTime = moment(startTimeIso).second(0).millisecond(0);
	const endTime = moment(endTimeIso).second(0).millisecond(0);

	if (repeatRule && repeatEndDateIso) {
		const rule = RRule.fromString(repeatRule);
		rule.options.dtstart = moment(startTimeIso).startOf('day').toDate();
		rule.options.until = moment(repeatEndDateIso).endOf('day').toDate();

		// Build instance times
		const duration = moment.duration(endTime.diff(startTimeIso));
		const startOfToday = moment().startOf('day');
		for (const date of rule.all()) {
			// Don't create instances in the past
			if (date < startOfToday.toDate()) {
				continue;
			}
			const instanceStart = moment(date).set({
				date: date.getUTCDate(),
				month: date.getUTCMonth(),
				year: date.getUTCFullYear(),
				hour: startTime.hour(),
				minute: startTime.minute(),
			});
			const instanceEnd = instanceStart.clone().add(duration);
			upcomingTimes.push({ start: instanceStart.toDate(), end: instanceEnd.toDate() });
		}
	} else {
		if (startTime.isAfter(moment())) {
			upcomingTimes.push({ start: startTime.toDate(), end: endTime.toDate() });
		}
	}

	// Remove today's entry if it's in the past
	if (upcomingTimes.length > 0 && upcomingTimes[0].start < new Date()) {
		upcomingTimes.shift();
	}

	return upcomingTimes;
};

export const getUpcomingFlexInstanceTimes = (
	flexPeriod: FlexPeriod,
	startDay: Date | undefined,
	timezone: string,
	repeatEndDateIso: string | null | undefined = null
): InstanceTimes[] => {
	startDay = startDay ?? new Date();
	const upcomingTimes: InstanceTimes[] = [];
	const endDate = repeatEndDateIso ? moment(repeatEndDateIso).toDate() : DEFAULT_REPEAT_END_DATE.toDate();

	// Iterate from tomorrow to next four weeks
	for (let date = startDay; date <= endDate; date = moment(date).add(1, 'day').toDate()) {
		const dayOfWeek = date.getDay();

		// Find matching schedules for the day of the week
		const matchingSchedules = (flexPeriod.schedules || []).filter((schedule) => {
			return schedule.days_of_week.includes(dayOfWeek);
		});

		// Generate start and end times for each matching schedule
		matchingSchedules.forEach((schedule) => {
			const startTime = moment
				.tz(date, timezone)
				.set({
					hour: schedule.start_hour,
					minute: schedule.start_minute,
				})
				.second(0)
				.millisecond(0);
			const endTime = moment
				.tz(date, timezone)
				.set({
					hour: schedule.end_hour,
					minute: schedule.end_minute,
				})
				.second(0)
				.millisecond(0);

			// Add the start and end times to the scheduleArray
			upcomingTimes.push({ start: startTime.toDate(), end: endTime.toDate() });
		});
	}

	// Remove today's entry if it's in the past
	if (upcomingTimes.length > 0 && upcomingTimes[0].start < new Date()) {
		upcomingTimes.shift();
	}

	return upcomingTimes;
};

//#endregion

//#region Repeat option helpers

export function getRepeatOptions(date: moment.Moment): DropdownOptions<string>[] {
	const options: DropdownOptions<string>[] = [
		{
			value: '',
			title: 'Does not repeat',
		},
	];

	const dayOfWeek = date.format('dddd');
	const nthOccurence = Math.ceil(date.date() / 7);

	const dailyRuly = new RRule({
		freq: RRule.DAILY,
		byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
	});
	options.push({ value: dailyRuly.toString(), title: 'Daily (M-F)' });

	const weeklyRule = new RRule({
		freq: RRule.WEEKLY,
		byweekday: DAY_OF_WEEK_TO_RRULE(dayOfWeek),
	});
	options.push({ value: weeklyRule.toString(), title: 'Every ' + dayOfWeek });

	const everyOtherWeekRule = new RRule({
		freq: RRule.WEEKLY,
		interval: 2,
		byweekday: DAY_OF_WEEK_TO_RRULE(dayOfWeek),
	});
	options.push({ value: everyOtherWeekRule.toString(), title: 'Every other ' + dayOfWeek });

	const monthOnOccrence = new RRule({
		freq: RRule.MONTHLY,
		byweekday: DAY_OF_WEEK_TO_RRULE(dayOfWeek).nth(nthOccurence),
	});
	options.push({
		value: monthOnOccrence.toString(),
		title: 'Monthly on the ' + nthOccurence + NTH(nthOccurence) + ' ' + dayOfWeek,
	});

	return options;
}

const NTH = (d: number) => {
	if (d > 3 && d < 21) return 'th';
	switch (d % 10) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
};

const DAY_OF_WEEK_TO_RRULE = (dayOfWeek: string): Weekday => {
	switch (dayOfWeek) {
		case 'Monday':
			return RRule.MO;
		case 'Tuesday':
			return RRule.TU;
		case 'Wednesday':
			return RRule.WE;
		case 'Thursday':
			return RRule.TH;
		case 'Friday':
			return RRule.FR;
		case 'Saturday':
			return RRule.SA;
		case 'Sunday':
			return RRule.SU;
		default:
			return RRule.MO;
	}
};

//#endregion

//#region Student sign-up window helpers

export interface SignUpWindowForm {
	signup_window_start: string;
	signup_window_end: string;
}

export enum SignupWindowState {
	BEFORE = 'before',
	OPEN = 'open',
	CLOSED = 'closed',
}

export const NO_LIMIT_START = '-100 years';
export const NO_LIMIT_END_VALUES = ['-1 second', '-00:00:01'];
export const UNTIL_SESSIONS_END = 'session_end';

// Updated options with match arrays
export const signUpOptions = [
	{ title: '1 month before', value: '-1 mons', matches: ['-1 mons'], order: 1, disabled: false },
	{ title: '2 weeks before', value: '-14 days', matches: ['-14 days'], order: 2, disabled: false },
	{ title: '1 week before', value: '-7 days', matches: ['-7 days'], order: 3, disabled: false },
	{ title: '1 day before', value: '-1 days', matches: ['-1 days'], order: 4, disabled: false },
	{ title: '1 hour before', value: '-1 hour', matches: ['-1 hour', '-01:00:00'], order: 5, disabled: false },
	{
		title: 'No limit',
		value: 'no_limit',
		matches: [NO_LIMIT_START, ...NO_LIMIT_END_VALUES],
		order: 6,
		disabled: false,
	},
	{ title: 'Until sessions end', value: UNTIL_SESSIONS_END, matches: [UNTIL_SESSIONS_END], order: 7, disabled: false },
];

export function getSignUpWindowLabel(value: string, isStart: boolean): string {
	if (isStart && value === NO_LIMIT_START) return 'As early as students want';
	if (!isStart && NO_LIMIT_END_VALUES.includes(value)) return 'Up until sessions start';
	if (!isStart && value === UNTIL_SESSIONS_END) return 'Up until sessions end';

	const match = signUpOptions.find((opt) => opt.matches.includes(value));
	return match ? `${match.title} sessions start` : 'Unknown';
}

export function getWindowSession(flexPeriod: FlexPeriod): SignUpWindowSessions {
	return {
		earliest: getSignUpWindowLabel(flexPeriod.signup_window_start, true),
		latest: getSignUpWindowLabel(flexPeriod.signup_window_end, false),
	};
}

export function getDisplayLabelForStart(value: string): string {
	if (value === NO_LIMIT_START) return 'as early as they want';
	const match = signUpOptions.find((opt) => opt.matches.includes(value));
	return match ? `starting ${match.title.toLowerCase()} sessions start` : '...';
}

export function getDisplayLabelForEnd(value: string): string {
	if (NO_LIMIT_END_VALUES.includes(value)) return 'the sessions start';
	if (value === UNTIL_SESSIONS_END) return 'the sessions end';

	const match = signUpOptions.find((opt) => opt.matches.includes(value));
	return match ? `${match.title.toLowerCase()} sessions start` : '...';
}

//#endregion
